import { use, useState } from 'react';
import { TeContainer, TeStep, TeSteps, TeText } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { PlaceholderContainer } from '@topcon/topcon-utils';

import { Section } from '../../../utils/styling';
import { ReviewServiceAgreementStep } from '../ReviewServiceAgreementStep';
import { AppRoutes } from '../../../app/routes/utils';
import { ReviewResultContextType } from '../../../providers/ReviewResultContext/review-result.types';
import { ReviewResultContext } from '../../../providers/ReviewResultContext/review-result.context';
import ReviewAgreementTypeStep from '../ReviewAgreementTypeStep';
import { useServiceAgreementWithId } from '../../../pages/Customers/customers.queries';
import { ServiceAgreementStatusEnum } from '../../../api/generated/pm';
import TermsAndConditionsStep from '../TermsAndConditionsStep';

type ServiceAgreementStep = 'companyReview' | 'agreementReview' | 'termsAndConditions';

function ServiceAgreementRequestStep() {
  const { t } = useTranslation();
  const [step, setStep] = useState<ServiceAgreementStep>('companyReview');
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setResult } = use<ReviewResultContextType>(ReviewResultContext);
  const {
    data: serviceAgreementData,
    isLoading: isServiceAgreementLoading,
    isError: isServiceAgreementError,
  } = useServiceAgreementWithId(id);

  if (!id || isServiceAgreementError) {
    navigate(AppRoutes.Overview);
    return;
  }

  const isReviewFlow = searchParams.get('flow') === 'review';
  const isAlreadyProcessed =
    serviceAgreementData?.serviceAgreementStatus === ServiceAgreementStatusEnum.ACTIVE ||
    serviceAgreementData?.serviceAgreementStatus === ServiceAgreementStatusEnum.DECLINED;

  if (!isReviewFlow && isAlreadyProcessed) {
    navigate(AppRoutes.Overview, { replace: true });
    return;
  }

  const handleAcceptAgreement = () => {
    setResult('accepted');
  };

  return (
    <TeContainer column grow padding="s" gap="xs" overflow="auto" alignItems="center">
      <Section $width="s">
        <TeContainer padding="m" fill grow justifyContent="space-between" alignItems="center">
          <TeText size={20} weight={700}>
            {t('serviceAgreement.request.title')}
          </TeText>
          <TeStepsWrapper>
            <TeStep
              text={t('companyReview')}
              icon={step === 'agreementReview' ? 'check_circle_outline' : 'circle'}
              activeByDefault
              active={step === 'companyReview'}
            />
            <TeStep
              text={t('serviceAgreement.review')}
              icon="circle"
              active={step === 'agreementReview'}
            />

            <TeStep
              text={t('termsAndConditions')}
              icon="circle"
              active={step === 'termsAndConditions'}
            />
          </TeStepsWrapper>
        </TeContainer>
      </Section>
      <Section $width="s">
        {isServiceAgreementLoading ? (
          <PlaceholderContainer hasLoader text={t('serviceAgreement.loading')} />
        ) : (
          <>
            <ReviewServiceAgreementStep
              serviceAgreement={serviceAgreementData}
              visible={step === 'companyReview'}
              onNextStep={() => setStep('agreementReview')}
            />
            <ReviewAgreementTypeStep
              id={id}
              visible={step === 'agreementReview'}
              onPreviousStep={() => setStep('companyReview')}
              onNextStep={() => setStep('termsAndConditions')}
            />
            <TermsAndConditionsStep
              id={id}
              visible={step === 'termsAndConditions'}
              onPreviousStep={() => setStep('agreementReview')}
              onNextStep={handleAcceptAgreement}
            />
          </>
        )}
      </Section>
    </TeContainer>
  );
}

const TeStepsWrapper = styled(TeSteps)`
  height: 100%;
`;

export default ServiceAgreementRequestStep;
